import React from 'react';
import { Cache, Viewer } from '@photo-sphere-viewer/core';
import { EquirectangularTilesAdapter } from '@photo-sphere-viewer/equirectangular-tiles-adapter';
import { MarkersPlugin } from '@photo-sphere-viewer/markers-plugin';
import '@photo-sphere-viewer/core/index.css';
import '@photo-sphere-viewer/markers-plugin/index.css';
import { navigationMarkerLabel, navigationMarkerSize } from './navigationMarker';
import { isTiledPanorama, panoramaSourceKey, toViewerPanorama } from './tiledPanorama';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function markerHtml(station) {
  const name = navigationMarkerLabel(station.name);
  return `<button type="button" class="nav-pin" title="${escapeHtml(
    name
  )}" aria-label="${escapeHtml(`${name}，跳转场景`)}">${escapeHtml(name)}</button>`;
}

function markerStateKey(stations) {
  return JSON.stringify((stations || []).map((station) => [
    station.id,
    station.name,
    station.yaw,
    station.pitch,
  ]));
}

export default function PanoramaViewer({
  src,
  stations,
  onPlace,
  onSelectStation,
  onError,
  onContextLost,
}) {
  const containerRef = React.useRef(null);
  const viewerRef = React.useRef(null);
  const markersRef = React.useRef(null);
  const readyRef = React.useRef(false);
  const lastSourceKeyRef = React.useRef(null);
  const panorama = React.useMemo(() => toViewerPanorama(src), [src]);
  const sourceKey = React.useMemo(() => panoramaSourceKey(src), [src]);
  const stationsKey = React.useMemo(() => markerStateKey(stations), [stations]);
  const sourceMode = isTiledPanorama(src) ? 'tiles' : 'image';
  const latestSourceRef = React.useRef(panorama);
  const latestSourceKeyRef = React.useRef(sourceKey);
  latestSourceRef.current = panorama;
  latestSourceKeyRef.current = sourceKey;
  const handlersRef = React.useRef({ onPlace, onSelectStation, onError, onContextLost, stations });

  React.useEffect(() => {
    handlersRef.current = { onPlace, onSelectStation, onError, onContextLost, stations };
  }, [onContextLost, onError, onPlace, onSelectStation, stations]);

  const renderMarkers = React.useCallback(() => {
    const plugin = markersRef.current;
    if (!plugin || !readyRef.current) return;

    try {
      plugin.clearMarkers();
      (handlersRef.current.stations || []).forEach((station) => {
        const markerSize = navigationMarkerSize(station.name, containerRef.current?.clientWidth);
        plugin.addMarker({
          id: station.id,
          position: { yaw: station.yaw, pitch: station.pitch },
          html: markerHtml(station),
          anchor: 'center center',
          size: markerSize,
          tooltip: `${escapeHtml(station.name || '前往')}<br/>点击跳转并播报`,
          data: { stationId: station.id },
        });
      });
    } catch (error) {
      handlersRef.current.onError?.(error);
    }
  }, []);

  React.useEffect(() => {
    if (!containerRef.current || !latestSourceRef.current) return undefined;

    // Editing sessions should only keep the active panorama in memory.
    Cache.enabled = false;
    Cache.items = {};

    let viewer;
    let rendererCanvas = null;
    let markersPlugin = null;
    let disposed = false;
    let handleReady;
    let handlePanoramaError;
    let handleViewerClick;
    let handleMarkerSelected;
    let handleContextLost;

    // React Strict Mode probes effects by mounting and immediately cleaning them up.
    // Deferring PSV construction prevents that probe from allocating a transient WebGL context.
    const initialize = () => {
      if (disposed || !containerRef.current) return;
      try {
        viewer = new Viewer({
          container: containerRef.current,
          navbar: false,
          defaultZoomLvl: 0,
          adapter: sourceMode === 'tiles'
            ? EquirectangularTilesAdapter.withConfig({ baseBlur: false, antialias: true, showErrorTile: true })
            : undefined,
          plugins: [[MarkersPlugin, { markers: [], clickEventOnMarker: false }]],
        });
      } catch (error) {
        handlersRef.current.onError?.(error);
        return;
      }
      markersPlugin = viewer.getPlugin(MarkersPlugin);
      viewerRef.current = viewer;
      markersRef.current = markersPlugin;
      lastSourceKeyRef.current = latestSourceKeyRef.current;

      handleReady = () => {
        readyRef.current = true;
        rendererCanvas = viewer.renderer?.canvas || containerRef.current?.querySelector('canvas');
        rendererCanvas?.addEventListener('webglcontextlost', handleContextLost, false);
        renderMarkers();
      };
      handlePanoramaError = (event) => {
        handlersRef.current.onError?.(event?.error || new Error('全景加载失败'));
      };
      handleViewerClick = (event) => {
        const data = event?.data || event;
        const yaw = data?.yaw;
        const pitch = data?.pitch;
        if (yaw == null || pitch == null) return;
        handlersRef.current.onPlace?.({ yaw, pitch });
      };
      handleMarkerSelected = ({ marker }) => {
        const stationId = marker?.config?.data?.stationId;
        if (stationId) handlersRef.current.onSelectStation?.(stationId);
      };
      handleContextLost = (event) => {
        event?.preventDefault?.();
        handlersRef.current.onContextLost?.();
      };

      viewer.addEventListener('ready', handleReady, { once: true });
      viewer.addEventListener('panorama-error', handlePanoramaError);
      viewer.addEventListener('click', handleViewerClick);
      markersPlugin?.addEventListener('select-marker', handleMarkerSelected);
      viewer.setPanorama(latestSourceRef.current, { transition: false }).catch((error) => {
        if (!disposed) handlersRef.current.onError?.(error);
      });
    };

    const initializeTimer = window.setTimeout(initialize, 0);

    return () => {
      disposed = true;
      window.clearTimeout(initializeTimer);
      if (!viewer) return;
      rendererCanvas?.removeEventListener('webglcontextlost', handleContextLost, false);
      markersPlugin?.removeEventListener('select-marker', handleMarkerSelected);
      viewer.removeEventListener('panorama-error', handlePanoramaError);
      viewer.removeEventListener('click', handleViewerClick);
      readyRef.current = false;
      markersRef.current = null;
      viewerRef.current = null;
      try {
        viewer.destroy();
      } catch (error) {
        // A lost WebGL context can make renderer cleanup throw; the stale instance is no longer retained.
        console.warn('VR viewer cleanup after context loss', error);
      }
    };
  }, [renderMarkers, sourceMode]);

  React.useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !panorama || lastSourceKeyRef.current === sourceKey) return;

    lastSourceKeyRef.current = sourceKey;
    viewer
      .setPanorama(panorama, { transition: false })
      .then(() => renderMarkers())
      .catch((error) => handlersRef.current.onError?.(error));
  }, [panorama, renderMarkers, sourceKey]);

  React.useEffect(() => {
    renderMarkers();
  }, [renderMarkers, stationsKey]);

  return <div ref={containerRef} className="panorama-viewer" data-testid="panorama-viewer" aria-label="VR 全景导览" />;
}
